import { ChevronDown } from 'lucide-react';
import type { ChatModel } from '@reflex/shared';
import { cn } from '../../lib/utils';
import { MODEL_MAP, type ModelConfig } from '../../lib/model-icons';
import { ModelProviderIcon } from './ModelProviderIcon';

function resolveConfig(id: string): ModelConfig | undefined {
  return MODEL_MAP.get(id as ChatModel);
}

export interface ModelPickerTriggerContentProps {
  /** Selected model id, or empty/undefined when nothing is chosen. */
  value?: string | null;
  /** Human label when the id is not in the chat catalog. */
  label?: string;
  placeholder?: string;
  /**
   * `form` — provider icon + full label.
   * `compact` / `header` — catalog icon + responsive name (short on narrow).
   */
  size?: 'form' | 'compact' | 'header';
  className?: string;
}

/**
 * Shared closed-trigger chrome for model pickers: brand/provider icon, model
 * name, and chevron. Used by {@link ModelPicker} and the agent-settings left
 * pill so launch and automations generate stay visually aligned.
 */
export function ModelPickerTriggerContent({
  value,
  label,
  placeholder = 'Select model',
  size = 'compact',
  className,
}: ModelPickerTriggerContentProps) {
  const activeConfig = value ? resolveConfig(value) : undefined;
  const selectedLabel = label ?? activeConfig?.name ?? value;
  const isForm = size === 'form';
  const isCompact = size === 'compact';

  return (
    <>
      {value ? (
        isForm ? (
          <>
            <ModelProviderIcon model={value} />
            <span className={cn('truncate', className)}>{selectedLabel}</span>
          </>
        ) : (
          <>
            <span className={cn(activeConfig && !activeConfig.isBrandSvg && activeConfig.color)}>
              {activeConfig ? (
                <activeConfig.icon
                  className={cn(
                    isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4',
                    !activeConfig.isBrandSvg && activeConfig.color,
                  )}
                />
              ) : (
                <ModelProviderIcon model={value} />
              )}
            </span>{' '}
            <span className={cn('truncate font-medium text-foreground/90', className)}>
              {activeConfig ? (
                <>
                  <span className="hidden sm:inline">{activeConfig.name}</span>
                  <span className="sm:hidden">{activeConfig.shortName}</span>
                </>
              ) : (
                selectedLabel
              )}
            </span>
          </>
        )
      ) : (
        <span className="truncate text-muted-foreground">{placeholder}</span>
      )}
      <ChevronDown
        className={cn(
          'h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-transform group-data-[state=open]:rotate-180',
          (isForm || isCompact) && 'ml-auto',
          isForm && 'text-muted-foreground',
        )}
      />
    </>
  );
}
